import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { NumberField, NumberInputTypes } from '../../../../../../global/components/form-element/number-field';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import { planAdaptionTableStyles } from '../../../../../../Global/constants/theme/styles/tables/plan-adoption-table';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { Institution } from '../../../../../../global/models/institution-model';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import AdoptingInstitutionView from '../display-components/adopting-institution-view';
import ContributionAmountField from '../display-components/contribution-amount-field';
import { ContributionType, EmployerContributionStatusType, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, ParticipantModel, QccoStatus } from '../models/plan-adoption-form-models';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';
import { PlanParticipantRowHeader } from './plan-participant-row-header';
import { PlanParticipantRowView } from './plan-participant-row-view';
import { FormItem } from '../../../../../../global/components/layout/form-item';
import { headerIconStyles } from '../../../../../../global/constants/theme/styles/components/header-icon-styles';

interface Props {
    wizard: QccoPlanAdoptionWizard;
}

function QccoAdoptionDetailsForm(props: Props) {

    const maxParticipants = 20;
    const { wizard } = props;
    const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(content?.gpsType);
    const apiErrors = useApiResponseError();
    const currentInstitution = useSelector<TcsmStates, Institution>(state => state?.institutionState?.currentInstitution);
    const initialValues: LayDcPlanFormModel = wizard?.getFormInitialValue();
    const validationSchema = wizard?.getFormValidationSchema();

    const adoptionDateOptions = wizard?.getAdoptionDateOptions();
    const employerContributionStatusOptions = wizard?.getEmployerContributionStatusOptions();
    const memberTypeOptions = wizard?.getMemberTypeOptions();
    const contributionAmountTypeOptions = wizard?.getContributionAmountTypeOptions();

    const qccoStatus: QccoStatus = wizard?.calculateQccoStatus(initialValues);
    const tableClasses = planAdaptionTableStyles();
    const buttonClasses = buttonStyles();
    const deferralClasses = headerIconStyles();
    const hasEmployerContributionStatus = content?.hasEmployerContributionStatus;
    const hasMemberType = content?.hasMemberType;

    const isIndemnificationComplete = wizard?.isIndemnificationComplete();

    function addParticipant(formProps: FormikProps<LayDcPlanFormModel>): void {

        const participants = formProps.values?.participants;

        if (!participants || participants?.length > maxParticipants) return;

        const lastItemId = participants.length === 0 ? -1 : participants[participants.length - 1].id;

        const newValues = [
            ...formProps.values.participants,
            {
                id: lastItemId + 1,
                participant_name: '',
                contribution_type: '',
            }];

        formProps.setFieldValue('participants', newValues);
    }

    function onRemoveParticipant(formProps: FormikProps<LayDcPlanFormModel>, participant: ParticipantModel): void {

        const newValues = formProps.values?.participants?.filter(p => p.id !== participant.id);

        formProps.setFieldValue('participants', newValues);
    }

    function onParticipantContributionTypeChange(formProps: FormikProps<LayDcPlanFormModel>, modifiedParticipant: ParticipantModel, newContributionType: ContributionType): void {

        const participants = formProps.values?.participants;

        if (!participants) return;

        const newContributionValueType = wizard?.getMappedContributionAmountTypeForContributionType(newContributionType);

        const newValues = participants?.map(participant => {
            if (participant.id === modifiedParticipant.id) {
                return {
                    ...participant,
                    contribution_type: newContributionType,
                    contribution_value_type: newContributionValueType,
                };
            }
            return participant;
        });

        formProps.setFieldValue('participants', newValues);
    }

    function renderMultipleContributionsParticipants(formProps: FormikProps<LayDcPlanFormModel>) {
        if (!formProps.values?.allow_multiple_contributions) return null;

        const canAddParticipant = formProps.values?.participants?.length < maxParticipants;

        return (
            <DividerSection topDivider >
                <WysiwygWithReactLink content={content?.adoptMultipleContributionHelper} />
                <ContentSpacer>
                    <div className={tableClasses.tableWrapper}>
                        <PlanParticipantRowHeader wizard={wizard} />
                        <>
                            {
                                formProps?.values?.participants?.map((participant, participantIndex) =>
                                    <PlanParticipantRowView
                                        key={`${participant.id}-${participantIndex}`}
                                        index={participantIndex}
                                        fieldName='participants'
                                        formProps={formProps}
                                        participant={participant}
                                        wizard={wizard}
                                        onContributionTypeChanged={onParticipantContributionTypeChange}
                                        onRemove={onRemoveParticipant} />,
                                )
                            }
                        </>
                    </div>
                </ContentSpacer>
                {
                    canAddParticipant ?
                        <ContentSpacer>
                            <button className={buttonClasses.linkButton} type='button' onClick={() => addParticipant(formProps)}>{content?.contributionAddEmployeeLabel}</button>
                        </ContentSpacer> :
                        <WysiwygWithReactLink content={content?.participantsMaxMessage} />
                }

            </DividerSection>);
    }

    function renderMultipleContributions(formProps: FormikProps<LayDcPlanFormModel>) {
        if (qccoStatus !== QccoStatus.Qualified) return null;

        const allow_multiple_contributions = !!formProps.values?.allow_multiple_contributions;

        return <div className='form-item'>
            <ContentSpacer>
                <ContentSpacer>
                    <WysiwygWithReactLink content={content?.adoptMultipleContributionDescription} />
                    <FormField
                        name='allow_multiple_contributions'
                        label={content?.allowMultipleContributionsCheckBoxLabel}
                        component='checkBox'
                        value={allow_multiple_contributions}
                        checked={allow_multiple_contributions}
                        touched={formProps.touched}
                        errors={formProps.errors}
                    />
                </ContentSpacer>
                {renderMultipleContributionsParticipants(formProps)}
            </ContentSpacer>
        </div>;
    }

    function renderSalaryDeferralsContent() {
        return (
            <div className={deferralClasses.iconHeader}>
               {/* <ArrowForwardIcon />*/}
                <svg aria-hidden='true' focusable='false'>
                    <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#circle-arrow-icon' />
                </svg>
            <WysiwygWithReactLink content={content?.adoptEmployeeSalaryDeferText} />
            </div>
        );
    }

    function renderMemberType(formProps: FormikProps<LayDcPlanFormModel>) {
        if (!hasMemberType) return null;

        return <>

            <FormField
                name='member_type'
                label={content?.adoptMemberTypeLabel}
                component='radioGroup'
                options={memberTypeOptions}
                touched={formProps.touched}
                errors={formProps.errors}
                tooltip={content?.adoptMemberHelper}
                required
            />
        </>;
    }

    function renderEmployerContributionStatus(formProps: FormikProps<LayDcPlanFormModel>) {
        if (!hasEmployerContributionStatus) return null;

        return <>
            <div className='form-item'>
                <WysiwygWithReactLink content={content?.adoptEmployerContributionStatus} />
            </div>
            <FormField
                name='employer_contribution_status'
                label={content?.adoptEmployerContributionTitle}
                component='radioGroup'
                options={employerContributionStatusOptions}
                touched={formProps.touched}
                errors={formProps.errors}
                required
            />
            {
                formProps.values?.employer_contribution_status === EmployerContributionStatusType.Discretionary &&
                <ContentSpacer>
                    <Banner
                        banner={{
                            type: 'warning',
                            label: <p><strong>{content?.adoptMultipleContributionWarningTitle}</strong></p>,
                            description: <WysiwygWithReactLink content={content?.adoptMultipleContributionWarningMessage} />,
                        }}
                    />

                </ContentSpacer>
            }
        </>;
    }

    function renderEmployerBaseContribution(formProps: FormikProps<LayDcPlanFormModel>) {

        if (hasEmployerContributionStatus && formProps.values?.employer_contribution_status === EmployerContributionStatusType.Discretionary) return null;

        return <ContributionAmountField
            labelText={content?.adoptEmployerBaseContributionLabel}
            formProps={formProps}
            amountTypePropertyName='employer_base_contribution_amount_type'
            amountPropertyName='employer_base_contribution_amount'
            percentagePropertyName='employer_base_contribution_percentage'
            isRequired={!hasEmployerContributionStatus}
            hasTypeSelector={hasEmployerContributionStatus}
            contributionAmountTypeOptions={contributionAmountTypeOptions} />;
    }

    function renderEmployerMatchContribution(formProps: FormikProps<LayDcPlanFormModel>) {

        if (hasEmployerContributionStatus && formProps.values?.employer_contribution_status === EmployerContributionStatusType.Discretionary) return null;

        return <ContributionAmountField
            labelText={content?.adoptEmployerMatchContributionLabel}
            formProps={formProps}
            amountTypePropertyName='employer_match_contribution_amount_type'
            amountPropertyName='employer_match_contribution_amount'
            percentagePropertyName='employer_match_contribution_percentage'
            isRequired={!hasEmployerContributionStatus}
            hasTypeSelector={hasEmployerContributionStatus}
            contributionAmountTypeOptions={contributionAmountTypeOptions} />;
    }

    if (!initialValues?.qccoQuestion1 || !qccoStatus || !isIndemnificationComplete) {
        return <Redirect to={wizard?.wizardOptions?.featureRootPath} />;
    }

    return (
        <PageContainer>
            <MainContent>
                <Formik
                    initialValues={initialValues}
                    onSubmit={
                        (values) => wizard?.onSubmit(
                            {
                                values,
                                clientId: currentInstitution?.org_client_nbr,
                            })
                    }
                    enableReinitialize
                    initialErrors={apiErrors}
                    validationSchema={validationSchema}
                >
                    {(formProps: FormikProps<LayDcPlanFormModel>) => (
                        <>
                            <Form>
                                <Grid>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel({
                                            values: formProps.values,
                                            clientId: currentInstitution?.org_client_nbr,
                                        })}
                                        submitDisabled={wizard?.isSubmitDisabled(formProps.values)}
                                        preventButtonDisableValidation={wizard?.preventButtonDisableValidation()}
                                        submitText={content?.submitLabel}
                                        cancelText={content?.cancelLabel}
                                    >
                                        <PageHeader
                                            headerText={content?.pageTitle}
                                            description={content?.PageDescription}
                                        />
                                        <Hidden mdUp>
                                            <SideBarContent>
                                                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                            </SideBarContent>
                                        </Hidden>
                                        <DividerSection removeDivider >
                                            <AdoptingInstitutionView institution={currentInstitution} clientNumberLabel={content?.clientIdLabel} sectionTitle={content?.adoptingInstitutionLabel} />
                                            <ContentSpacer>
                                                <FormGroup>
                                                    <FormField
                                                        name='adoption_date'
                                                        label={content?.adoptionDateLabel}
                                                        component="input"
                                                        options={adoptionDateOptions}
                                                        touched={formProps.touched}
                                                        errors={formProps.errors}
                                                        required
                                                        placeholder={labelSettings?.selectDefaultText}
                                                        select
                                                    />

                                                    <FormItem>
                                                        {renderSalaryDeferralsContent()}
                                                    </FormItem>
                                                    {renderMemberType(formProps)}

                                                    <FormItem>
                                                        <WysiwygWithReactLink content={content?.adoptEmployerContributionText} />
                                                    </FormItem>
                                                    <FormItem>
                                                        <NumberField
                                                            name='minimum_scheduleds_hours'
                                                            label={content?.adoptMinScheduleHoursLabel}
                                                            type={NumberInputTypes.Integer}
                                                            formikProps={formProps}
                                                        />

                                                        <WysiwygWithReactLink content={content?.adoptMinScheduleHoursHelperText} />
                                                    </FormItem>
                                                    {renderEmployerContributionStatus(formProps)}

                                                    {renderEmployerBaseContribution(formProps)}

                                                    {renderEmployerMatchContribution(formProps)}
                                                </FormGroup>
                                            </ContentSpacer>

                                            {renderMultipleContributions(formProps)}

                                        </DividerSection>
                                    </FormWrapper>
                                </Grid>
                            </Form>
                        </>
                    )}
                </Formik>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );

}

export default QccoAdoptionDetailsForm;